// src/pages/GameLevelsPage.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, setDoc, deleteDoc, writeBatch, addDoc } from 'firebase/firestore';
import LevelEditor from '../components/LevelEditor';
import './GameLevelsPage.css';
import ImportJsonPopup from '../components/ImportJsonPopup';

// Default empty puzzle structure for creating a new level
const NEW_PUZZLE_TEMPLATE = {
  puzzleId: '',
  difficulty: 'easy',
  level: 1,
  width: 4,
  height: 4,
  flatGrid: Array.from({ length: 16 }, () => ({ n: 0, b: [] })),
  solutionPath: [],
  hints: [],
};

const GameLevelsPage = () => {
  const [puzzles, setPuzzles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPuzzle, setEditingPuzzle] = useState(null);

  const [showImportPopup, setShowImportPopup] = useState(false);
  const levelsCollectionRef = collection(db, "puzzles");

  const fetchPuzzles = async () => {
    setLoading(true);
    const puzzleSnapshot = await getDocs(collection(db, 'puzzles'));
    const puzzleList = puzzleSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log("Fetched puzzles:", puzzleList); // --- IGNORE ---
    puzzleList.sort((a, b) => a.difficulty.localeCompare(b.difficulty) || a.level - b.level);
    setPuzzles(puzzleList);
    setLoading(false);
  };

  useEffect(() => {
    fetchPuzzles();
  }, []);

  const handleSave = async (puzzleData) => {
    if (!puzzleData.puzzleId) {
      alert('Puzzle ID cannot be empty.');
      return;
    }
    // Firestore uses the 'id' as the document key, not puzzleId from the data
    const { id, ...dataToSave } = puzzleData;
    console.log("Saving puzzle data:", puzzleData); // --- IGNORE ---
    await setDoc(doc(db, 'puzzles', puzzleData.puzzleId), dataToSave);

    setEditingPuzzle(null); // Hide the editor
    fetchPuzzles(); // Refresh the list
  };

  const handleDelete = async (puzzleId) => {
    if (window.confirm(`Are you sure you want to delete puzzle ${puzzleId}?`)) {
      await deleteDoc(doc(db, 'puzzles', puzzleId));
      fetchPuzzles(); // Refresh the list
    }
  };

  const getNextLevel = (difficulty) => {
    const relevantPuzzles = puzzles.filter(p => p.difficulty === difficulty);
    if (relevantPuzzles.length === 0) return 1;
    const maxLevel = Math.max(...relevantPuzzles.map(p => p.level));
    return maxLevel + 1;
  };

  if (loading) {
    return <div className="page-content">Loading levels...</div>;
  }

  if (editingPuzzle) {
    return (
      <div className="page-content">
        <LevelEditor
          initialData={editingPuzzle}
          onSave={handleSave}
          onCancel={() => setEditingPuzzle(null)}
          getNextLevel={getNextLevel}
        />
      </div>
    );
  }

  const handleBulkImport = async (jsonData) => {
    // Sử dụng Firestore Batch để ghi nhiều document cùng lúc an toàn
    const batch = writeBatch(db);
    let count = 0;

    const difficulties = ['easy', 'medium', 'hard'];

    difficulties.forEach(diff => {
      if (jsonData[diff] && Array.isArray(jsonData[diff])) {
        jsonData[diff].forEach(levelData => {
          // Tạo một reference document mới (tự động sinh ID)
          const newDocRef = doc(levelsCollectionRef);

          // Chuẩn bị dữ liệu để lưu, thêm trường 'difficulty'
          const dataToSave = {
            ...levelData,
            difficulty: diff, // Lưu độ khó vào document
            createdAt: new Date().toISOString(), // Thêm thời gian tạo (tùy chọn)
          };

          // Thêm thao tác set vào batch
          batch.set(newDocRef, dataToSave);
          count++;
        });
      }
    });

    if (count === 0) {
      alert("Không tìm thấy level nào hợp lệ trong JSON để import.");
      return;
    }

    // Thực thi batch
    await batch.commit();
    alert(`Đã import thành công ${count} levels!`);

    // Tải lại danh sách levels sau khi import
    fetchPuzzles();
  };

  return (
    <div className="page-content">
      <h1>Game Levels</h1>
      <div className="actions-bar">
        <button
          className="btn btn-primary"
          onClick={() => setEditingPuzzle(NEW_PUZZLE_TEMPLATE)}
        >
          Add New Level
        </button>
        <button
          className="btn"
          onClick={() => setShowImportPopup(true)}
          style={{ marginLeft: '10px', backgroundColor: '#f59e0b', color: 'white' }}
        >
          ⬆️ Import JSON
        </button>
      </div>

      <div className="table-container">
        <table className="levels-table">
          {/* Table Head (thead) is unchanged */}
          <tbody>
            {puzzles.map(puzzle => (
              <tr key={puzzle.id}>
                <td>{puzzle.id}</td>
                <td>{puzzle.difficulty}</td>
                <td>{puzzle.level}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn btn-primary" onClick={() => setEditingPuzzle(puzzle)}>
                      Edit
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDelete(puzzle.id)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showImportPopup && (
        <ImportJsonPopup
          onImport={handleBulkImport}
          onClose={() => setShowImportPopup(false)}
        />
      )}
    </div>
  );
};

export default GameLevelsPage;